import React, { useState } from 'react';
import { BookOpen, CheckCircle2 } from 'lucide-react';

export const RealOutputDemo: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'automacao' | 'financas'>('automacao');

  return (
    <section className="py-20 md:py-28 bg-brand-surface/30 border-y border-brand-border relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-xs font-semibold uppercase tracking-wider text-emerald-400 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            Casos Reais do Laboratório
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 mb-5 tracking-tight">
            Veja a Qualidade Real Compilada Pelo InfoForge
          </h2>
          <p className="text-base sm:text-lg text-brand-textMuted leading-relaxed">
            Exemplos completos gerados durante nossos testes rigorosos de certificação de produto. Sem maquiagem sintética.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-10">
          <div className="p-1.5 rounded-2xl bg-brand-surface border border-brand-border inline-flex gap-2">
            <button
              onClick={() => setActiveTab('automacao')}
              className={`px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                activeTab === 'automacao'
                  ? 'bg-brand-primary text-white shadow-md'
                  : 'text-brand-textMuted hover:text-white'
              }`}
            >
              Nicho Técnico B2B: Automação Industrial
            </button>
            <button
              onClick={() => setActiveTab('financas')}
              className={`px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                activeTab === 'financas'
                  ? 'bg-brand-primary text-white shadow-md'
                  : 'text-brand-textMuted hover:text-white'
              }`}
            >
              Nicho Prático B2C: Gestão Financeira Familiar
            </button>
          </div>
        </div>

        {/* Product Showcase Card */}
        <div className="rounded-3xl bg-brand-bg border border-brand-border overflow-hidden shadow-2xl max-w-5xl mx-auto">
          {/* Header Bar */}
          <div className="px-6 py-4 bg-brand-surface border-b border-brand-border flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-mono text-brand-textMuted">
                {activeTab === 'automacao' ? 'ID: BATCH-CERT-IND-001' : 'ID: BATCH-CERT-FIN-002'}
              </span>
              <span className="text-xs text-brand-textSubtle">•</span>
              <span className="text-xs text-brand-cyan font-medium">Exemplo gerado em ambiente de certificação</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-brand-primary/10 text-brand-primary border border-brand-primary/20">
                PDF + EPUB + Workbook + VSL
              </span>
            </div>
          </div>

          {/* Body Content */}
          <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left: Metadata & Deliverables */}
            <div className="lg:col-span-5 space-y-6">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-brand-primary">
                  {activeTab === 'automacao' ? 'Engenharia & Indústria 4.0' : 'Finanças & Economia Doméstica'}
                </span>
                <h3 className="text-2xl font-bold text-white mt-1">
                  {activeTab === 'automacao'
                    ? 'Automação Industrial Avançada: Da Fábrica aos Sistemas Ciberfísicos'
                    : 'Gestão Financeira Familiar: O Método Prático de Desendividamento e Reserva'}
                </h3>
                <p className="text-xs text-brand-textMuted mt-2 leading-relaxed">
                  {activeTab === 'automacao'
                    ? 'Um guia aprofundado para engenheiros e técnicos com foco em PLCs, SCADA, redes industriais e segurança operacional.'
                    : 'Um programa de 5 etapas para famílias recuperarem o controle financeiro, negociarem dívidas e criarem reservas seguras.'}
                </p>
              </div>

              <div className="space-y-3 pt-2">
                <div className="text-xs font-bold text-brand-text uppercase tracking-wider">
                  Entregáveis Validados Neste Pacote:
                </div>
                <div className="space-y-2 text-xs text-brand-textMuted">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Ebook Completo com 8 capítulos e sumário estruturado</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Workbook de 15 páginas com matrizes de aplicação</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Roteiro de VSL persuasivo de 3 atos (9 minutos de fala)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span>Sequência de 5 e-mails estratégicos de conversão</span>
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-brand-surface/70 border border-brand-border/60">
                <div className="text-[11px] font-mono text-brand-textSubtle mb-1">AUDITORIA DE CONSISTÊNCIA</div>
                <div className="text-xs text-brand-text leading-snug">
                  Zero contradições conceituais detectadas entre a promessa comercial e o conteúdo didático das páginas.
                </div>
              </div>
            </div>

            {/* Right: Excerpt Preview */}
            <div className="lg:col-span-7 rounded-2xl bg-brand-surface border border-brand-border p-6 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between pb-3 border-b border-brand-border/60 mb-4 text-xs text-brand-textSubtle">
                  <span className="font-mono flex items-center gap-1.5">
                    <BookOpen className="w-3.5 h-3.5 text-brand-cyan" />
                    Trecho Extraído do Ebook Master
                  </span>
                  <span className="text-[10px] uppercase font-semibold text-emerald-400">Humanizer Aplicado</span>
                </div>

                {activeTab === 'automacao' ? (
                  <div className="space-y-3 font-serif text-brand-text text-sm leading-relaxed">
                    <p className="font-sans text-xs uppercase font-bold text-brand-cyan tracking-wider">
                      Capítulo 3: Arquitetura de Redes Industriais Determinísticas
                    </p>
                    <p className="text-slate-300">
                      "Diferente das redes corporativas convencionais, onde a latência média é tolerável, na automação industrial o determinismo temporal é uma exigência de segurança física. Um atraso de milissegundos na resposta de uma parada de emergência via PROFINET pode significar danos irreversíveis ao maquinário ou à equipe em solo..."
                    </p>
                    <p className="text-slate-400 text-xs italic">
                      [Nota técnica: Trecho escrito com rigor terminológico, contextualização de risco real e sem clichês genéricos de IA.]
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 font-serif text-brand-text text-sm leading-relaxed">
                    <p className="font-sans text-xs uppercase font-bold text-brand-cyan tracking-wider">
                      Capítulo 2: A Anatomia do Orçamento Invisível
                    </p>
                    <p className="text-slate-300">
                      "O maior erro ao planejar o orçamento familiar não está no cálculo das contas fixas, mas no que chamamos de 'despesas invisíveis': pequenas assinaturas, taxas bancárias embutidas e compras fracionadas no cartão que passam despercebidas até a fatura fechar. O primeiro passo da recuperação financeira não é o corte brusco, mas o raio-x transparente..."
                    </p>
                    <p className="text-slate-400 text-xs italic">
                      [Nota pedagógica: Abordagem empática, sem promessas irrealistas e com foco em passos viáveis e imediatos.]
                    </p>
                  </div>
                )}
              </div>

              {/* Hash & Signature */}
              <div className="mt-6 pt-4 border-t border-brand-border/60 flex flex-wrap items-center justify-between gap-2 text-[11px] text-brand-textSubtle font-mono">
                <span>FORMATOS: PDF (300dpi) · EPUB 3.0</span>
                <span>STATUS: CERTIFICADO NO STORAGE</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
